import React, { useEffect, useState } from "react";
import { Box, Button, Collapse, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCan } from "../../auth/Can";
import { AgentStatsRow, getAgentStats, getQaSample, QaSampleRow } from "../../services/supportService";

/** Phase 2 §5.10: per-agent stats (handled, reply / resolve time, CSAT, reopen rate, QA) + tickets to review (low ratings first). */
const show = (v: number | null, suffix = "") => (v == null ? "—" : `${v}${suffix}`);

const QualityPanel: React.FC = () => {
    const { can } = useCan();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState<AgentStatsRow[]>([]);
    const [sample, setSample] = useState<QaSampleRow[]>([]);

    useEffect(() => {
        if (!open) return;
        getAgentStats(days).then((r) => setStats(r.data.agents)).catch(() => setStats([]));
        if (can("support:write")) getQaSample(Math.min(days, 14)).then((r) => setSample(r.data.tickets)).catch(() => setSample([]));
    }, [open, days]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ px: 2, pb: 1 }} data-testid="quality-panel">
            <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={() => setOpen((o) => !o)}>{open ? "Hide quality" : "Quality"}</Button>
                {open && (
                    <Select size="small" value={days} onChange={(e) => setDays(Number(e.target.value))} inputProps={{ "aria-label": "Quality period" }}>
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                )}
            </Stack>
            <Collapse in={open}>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Agent</TableCell><TableCell>Handled</TableCell><TableCell>Resolved</TableCell><TableCell>First reply</TableCell>
                            <TableCell>Resolve</TableCell><TableCell>CSAT</TableCell><TableCell>1–2 ★</TableCell><TableCell>Reopen</TableCell><TableCell>QA</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {stats.length === 0 && <TableRow><TableCell colSpan={9}>No assigned tickets in this period.</TableCell></TableRow>}
                        {stats.map((a) => (
                            <TableRow key={a.agentId}>
                                <TableCell>{a.name}</TableCell><TableCell>{a.handled}</TableCell><TableCell>{a.resolved}</TableCell>
                                <TableCell>{show(a.avgFirstReplyMin, " min")}</TableCell><TableCell>{show(a.avgResolveMin, " min")}</TableCell>
                                <TableCell>{a.csatAvg == null ? "—" : `${a.csatAvg} (${a.csatCount})`}</TableCell><TableCell>{a.lowCsat}</TableCell>
                                <TableCell>{show(a.reopenRate, "%")}</TableCell><TableCell>{a.qaAvg == null ? "—" : `${a.qaAvg}/5 (${a.qaCount})`}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {sample.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>To review</Typography>
                        {sample.map((t) => (
                            <Button key={t.id} size="small" onClick={() => navigate(`/support-ticket/${t.id}`)}>
                                {t.publicNumber || `#${t.id}`}{t.csatScore != null ? ` ★${t.csatScore}` : ""}
                            </Button>
                        ))}
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export default QualityPanel;
